import { useMemo } from "react"
import { useDispatch } from "react-redux"
import { useQuery } from "@tanstack/react-query"
import { Creators } from "model/apiWrapper"
import { useAccountAlias } from "resources/account"
import { rejectOnGraphqlErrors, type ResultWithApiMessages } from "gatherers/extract-graphql-error-message"
import type { IsoDate } from "helpers/types/dates"
import { generateQueryKey } from "../helpers/query-key"
import { parseIntradayToolbarTimes, type IntradayToolbarTimes } from "../helpers/intraday-toolbar-times"
import { useInvalidateQueryKeysOnChange } from "./use-intraday-query-invalidation"

export const INTRADAY_TOOLBAR_TIMES_QUERY = `query IntradayToolbarTimesQuery($modelId: String!, $positionSet: PositionSetInput!, $on: Date!) {
  model(id: $modelId) {
    intradaySimulation(positionSet: $positionSet, on: $on) {
      positionsAsOf
      pricesAsOf
      currency
      reportedPnl {
        totalReturn
        amount
        asOf
        isApplied
        basis
      }
    }
  }
}`

export interface UseIntradayToolbarTimesParams {
  modelId: string | null
  instrument: { id?: string, type?: string } | null | undefined
  on: IsoDate
  isEnabled: boolean
  intradayPricingAsOf: string | null
  lastIntradayBatchId: string | null
}

interface IntradayToolbarTimesResponse extends ResultWithApiMessages {
  data?: { model?: { intradaySimulation?: unknown } }
}

export function useIntradayToolbarTimes({ modelId, instrument, on, isEnabled, intradayPricingAsOf, lastIntradayBatchId }: UseIntradayToolbarTimesParams): IntradayToolbarTimes | null {
  const dispatch = useDispatch()
  const account = useAccountAlias()
  const instrumentId = instrument?.id ?? null
  const instrumentType = instrument?.type ?? null
  const isQueryEnabled = isEnabled && !!modelId && !!instrumentId && !!instrumentType

  const queryKey = useMemo(
    () => generateQueryKey("intradayToolbarTimes", { modelId, instrumentId, instrumentType, on }),
    [modelId, instrumentId, instrumentType, on],
  )
  const invalidationKeys = useMemo(() => [queryKey], [queryKey])

  const result = useQuery({
    queryKey,
    enabled: isQueryEnabled,
    meta: { instrumentId, instrumentType },
    // Keeps the times up while a new day loads, but never shows another portfolio's times.
    placeholderData: (previousData, previousQuery) => (
      previousQuery?.meta?.instrumentId === instrumentId && previousQuery?.meta?.instrumentType === instrumentType ? previousData : undefined
    ),
    // Whole portfolio: the reported P&L ignores the dashboard filter, base and hedge.
    queryFn: ({ signal }) => new Promise<IntradayToolbarTimesResponse>((resolve, reject) => {
      const variables = { modelId, positionSet: { id: instrumentId, type: instrumentType }, on }
      dispatch(Creators.graphql(account, INTRADAY_TOOLBAR_TIMES_QUERY, variables, rejectOnGraphqlErrors(resolve, reject), reject, signal))
    }),
    select: (response: IntradayToolbarTimesResponse) => parseIntradayToolbarTimes(response?.data?.model?.intradaySimulation),
  })

  useInvalidateQueryKeysOnChange(intradayPricingAsOf, invalidationKeys)
  useInvalidateQueryKeysOnChange(lastIntradayBatchId, invalidationKeys)

  return isQueryEnabled ? result.data ?? null : null
}
